import { extendTheme, theme, ThemeConfig } from "@chakra-ui/react";
import { createBreakpoints, getColor, mode, Styles } from "@chakra-ui/theme-tools";
import { Global } from "@emotion/react";
import { StyleFunctionProps } from "@chakra-ui/theme-tools/dist/declarations/src/component";
import colors from "./APColorConfig";
import semanticTokens from "./APTokensConfig";

import { tinycolor } from "@ctrl/tinycolor"
import { apButtonConfig } from "./APButtonConfig";
import { mergeDeep } from "../utils";

export const Fonts = () => (
  <Global
    styles={`
    html {
      font-size:16px;
    }
    @font-face {
      font-family: 'Chromatica';
      src: url('${process.env.REACT_APP_BASE_URL}/fonts/ChromaticaModifiee-Regular.eot');
      src: url('${process.env.REACT_APP_BASE_URL}/fonts/ChromaticaModifiee-Regular.woff') format('woff'),
           url('${process.env.REACT_APP_BASE_URL}/fonts/ChromaticaModifiee-Regular.woff2') format('woff2');
    }
    @font-face {
      font-family: 'Chromatica';
      src: url('${process.env.REACT_APP_BASE_URL}/fonts/ChromaticaModifiee-Medium.eot');
      src: url('${process.env.REACT_APP_BASE_URL}/fonts/ChromaticaModifiee-Medium.woff') format('woff'),
           url('${process.env.REACT_APP_BASE_URL}/fonts/ChromaticaModifiee-Medium.woff2') format('woff2');
      font-weight: 500;
    }
    @font-face {
      font-family: 'Chromatica';
      src: url('${process.env.REACT_APP_BASE_URL}/fonts/ChromaticaModifiee-Bold.eot');
      src: url('${process.env.REACT_APP_BASE_URL}/fonts/ChromaticaModifiee-Bold.woff') format('woff'),
           url('${process.env.REACT_APP_BASE_URL}/fonts/ChromaticaModifiee-Bold.woff2') format('woff2');
      font-weight: 700;
    }
    @font-face {
      font-family: 'Chromatica';
      src: url('${process.env.REACT_APP_BASE_URL}/fonts/ChromaticaModifiee-Black.eot');
      src: url('${process.env.REACT_APP_BASE_URL}/fonts/ChromaticaModifiee-Black.woff') format('woff'),
           url('${process.env.REACT_APP_BASE_URL}/fonts/ChromaticaModifiee-Black.woff2') format('woff2');
      font-weight: 900;
    }
      `}
  />
);

/*
[
  0 > px, > 480 px, > 748 px, > 992 px]

*/

export const BREAKPOINTS_VALUES = {
  xs: 384, //384
  sm: 480, //480
  md: 768, //768
  lg: 992, //992
  xl: 1280, //1280
}

const breakpoints = createBreakpoints({
  xs: "24em", //384
  sm: "30em", //480
  md: "48em", //768
  lg: "62em", //992
  xl: "80em", //1280
});

// setup light/dark mode global defaults
const styles: Styles = {
  global: (props) => ({
    body: {
      bg: mode("white", "black")(props),
    },
  }),
};

const config: ThemeConfig = {
  initialColorMode: "light",
  useSystemColorMode: false,
  cssVarPrefix: 'ap',
};

const apTheme = extendTheme(
  {
    config: config,
    semanticTokens,
    colors
  },
  {
    fonts: {
      body: "Chromatica,barlow,sans-serif",
      heading: "Chromatica, sans-serif",
    },
    breakpoints,
    layerStyles: {
      pageHeader: {
        marginTop: "1rem",
        alignContent: "center",
        height: "fit-content"
      },
      scrollPageContainer: {
        minHeight: "min-content",
        height: "auto",
        width: "100%",
        flexGrow: 1,
        padding: "0 1rem",
        alignItems: "stretch",
        justifyContent: "space-between",
        flexDirection: "column",
        gap: "4",
      },
      pageContainer: {
        height: "100%",
        width: "100%",
        minHeight: "32rem",
        maxHeight: "100%",
        overflow: "visible",
        padding: "0 1rem",
        alignItems: "stretch",
        justifyContent: "space-between",
        scrollbarGutter: "stable both-edges",
        flexDirection: "column",
        gap: "4",
        position: "relative"
      },
      scrollContainer: {
        scrollMarginY: "1rem",
        paddingX: "0.25rem",
        marginX: "0.25rem",
        overflowY: "auto",
        overflowX: "hidden",
        scrollbarGutter: "stable both-edges",
      },
      scrollShadow: {
        position: "absolute",
        height: "0.5rem",
        left: 0,
        right: 0,
        bottom: 0,
        boxShadow: "inset 0px -1rem 0.5rem -1rem #D0CFCB"
      },
      pageContent: {
        bg: "white",
        borderRadius: "2xl",
        maxWidth: "container.md",
        position: "relative",
        alignItems: "stretch",
        alignSelf: "center",
        overflow: "visible",
        gap: "2",
        padding: "6",
        minHeight: "min-content",
      },
      pageFooter: {
        marginBottom: "1rem",
        alignContent: "center",
        height: "fit-content"
      },
      card: {
        bg: "white",
        borderRadius: "2rem",
        border: `solid 2px ${colors.lightGray["500"]}`,
        overflow: "clip",
      },
      cardContent: {
        width: "100%",
        height: "100%",
        flexDirection: "column",
        alignItems: "stretch",
        position: "relative",
        gap: "1.5",
        padding: "4",
        zIndex: 10,
      },
      textGlow: {
        textShadow: "" +
          "white 1px 1px 1px, " +
          "white -1px 1px 1px, " +
          "white 1px -1px 1px, " +
          "white -1px -1px 1px, " +
          "white 2px 2px 2px, " +
          "white -2px 2px 2px, " +
          "white 2px -2px 2px, " +
          "white -2px -2px 2px"
      },
      backgroundGlow: {
        position: "relative",
        overflow: "visible",
        bgColor: `white`,
        boxShadow: `0 0 0.25rem 0.75rem ${colors.white}`,
        rounded: "full",
      }
    },
    components: {
      Heading: {
        baseStyle: {
          fontWeight: "600",
        },
      },
      Text: {
        baseStyle: {
          fontWeight: "400",
        },
      },
      Link: {
        baseStyle: {
          transition: "ease-in-out all .2s",
          _hover: {
            color: "gray.400",
            textDecoration: "none",
          },
        },
      },
      Button: apButtonConfig,
      Modal: {
        parts: ['dialogContainer', 'dialog', 'closeButton'],
        baseStyle: {
          dialogContainer: {
            width: "100vw",
            height: "100vh",
            maxWidth: "100%",
            maxHeight: "100%",
            overflow: "hidden",
            margin: 0,
            padding: "1.125em",
            alignItems: "flex-start",
            justifyContent: "center",
          },
          dialog: {
            flex: "1 1 0%",
            height: "fit-content",
            maxWidth: "100%",
            minHeight: 0,
            maxHeight: ["calc(100% - 10vh)", "calc(100% - 14vh)", "calc(100% - 20vh)"],
            margin: ["5vh 0", "7vh 0", "10vh 0", "6vh 0"],
            overflow: "visible",
            borderRadius: "1.5rem",
            ["&>:first-child"]: {
              borderTopRadius: "1.5rem",
              // overflow: "clip"
            },
            ["&>:last-child"]: {
              borderBottomRadius: "1.5rem",
              // overflow: "clip"
            }
          },
          header: {
            maxHeight: "max(12vh,15%)",
            bg: "lightGray.500",
            paddingY: ["0.75rem", "1rem", "1.25rem"],
            paddingX: ["2rem"],
          },
          body: {
            position: "relative",
            maxHeight: "calc(100% - 24vh)",
            display: "flex",
            padding: ["0.75rem", "1rem", "1.25rem"],
            flex: "1 1 0%",
            height: "fit-content",
            flexDirection: "column",
            alignItems: "center",
            overflow: "visible",
            bg: "white",
          },
          footer: {
            maxHeight: "max(12vh,15%)",
            bg: "lightGray.500",
            padding: ["0.75rem", "1rem", "1.25rem"],
            alignItems: "stretch",
          },
          closeButton: {},
        },
        variants: {
          full: () => ({
            dialogContainer: {
              width: "100vw",
              height: "100vh",
              maxWidth: "100%",
              maxHeight: "100%",
              overflow: "hidden",
              margin: 0,
              padding: 0,
            },
            dialog: {
              flex: "1 1 0%",
              height: "100%",
              maxWidth: "100%",
              minHeight: 0,
              maxHeight: "100%",
              margin: "0",
              overflow: "visible",
              borderRadius: "0"
            },
            header: {
              maxHeight: "max(12vh,15%)",
              bg: "lightGray.500",
              padding: ["0.75rem", "1rem", "1.25rem"],
            },
            body: {
              position: "relative",
              maxHeight: "100%",
              display: "flex",
              padding: ["0.75rem", "1rem", "1.25rem"],
              flex: "1 1 0%",
              height: "fit-content",
              flexDirection: "column",
              alignItems: "center",
              overflow: "visible",
              bg: "white",
            },
          }),
          exerciseResponseModal: () => ({
            dialog: {
              borderRadius: "0 !important",
            },
            overlay: {
              background: "transparent"
            }
          }),
          splashModal: () => ({
            dialogContainer: {
              alignItems: "center",
            },
            dialog: {
              maxHeight: "calc(100% - 10vh)",
              margin: "5vh 0",
            },
            header: {
              padding: "1.5rem 1.5rem 0 1.5rem",
            },
          }),
          noNetworkModal: () => ({
            dialogContainer: {
              alignItems: "center",
            },
            dialog: {
              maxHeight: "calc(100% - 10vh)",
              margin: "5vh 0",
            },
            header: {
              padding: "1.5rem 1.5rem 0 1.5rem",
            },
          }),
          settingModal: () => ({
            dialog: {
              margin: "3rem 0",
            }
          }),
          seamlessBody: () => ({
            body: {
              padding: 0,
              alignItems: "stretch"
            },
          })

        }
      },
      Drawer: {
        parts: ['dialog', "header", "body", "footer"],
        baseStyle: {
          dialog: {
            borderRadius: "0",
          },
          header: {
            display: "flex",
            zIndex: 1,
            flexDir: "row",
            boxShadow: "0 0 0.25rem 0.25rem var(--ap-colors-blackAlpha-200)",
            padding: ["0.75rem", "1rem", "1.25rem"],
          },
          body: {
            padding: ["0.75rem", "1rem", "1.25rem"],
          },
          footer: {
            padding: ["0.75rem", "1rem", "1.25rem"],
          },
        },
      },
      Checkbox: {
        parts: ['icon', 'control'],
        baseStyle: (props: StyleFunctionProps) => ({
          icon: {
            color: "black",
          },
          control: {
            border: "1px solid",
            borderRadius: "2px",
            borderColor: getColor(apTheme, `${props.colorScheme}.fg`),
            bg: getColor(apTheme, `${props.colorScheme}.subtle`),
            _checked: {
              borderColor: getColor(apTheme, `${props.colorScheme}.focus`),
              bg: getColor(apTheme, `${props.colorScheme}.fg`),
            }
          }
        }),
      },
      Input: {
        parts: ['field', 'addon'],
        baseStyle: {
          textAlign: "center",
          borderRadius: "lg",
        },
        variants: {
          flushed: (props: StyleFunctionProps) => {
            const baseColor = tinycolor(getColor(props.theme, `${props.colorScheme}.fg`, `${props.colorScheme}.500`))

            return {
              field: {
                ...theme.components.Input.variants.flushed(props).field,
                textAlign: "center",
                color: getColor(apTheme, `${props.colorScheme}.contrast`, baseColor.isDark() ? `white` : 'black'),
                borderBottom: "1px",
                borderRadius: "xl",
                borderColor: getColor(apTheme, `${props.colorScheme}.focus`, `${props.colorScheme}.600`),
                bg: getColor(apTheme, `${props.colorScheme}.fg`, `${props.colorScheme}.500`),
                _focus: {
                  borderColor: "transparent",
                  boxShadow: "none",
                },
              },
            }
          },
          outline: (props: StyleFunctionProps) => {
            const baseColor = tinycolor(getColor(props.theme, `${props.colorScheme}.fg`, `${props.colorScheme}.500`))

            const shadowHighlight = baseColor.getLuminance() < 0.4 ? baseColor.lighten(10) : baseColor.darken(7);

            const bgColor = tinycolor(colors.white);
            const highlight = bgColor.darken(5);
            const active = bgColor.darken(15);

            const field = {
              color: getColor(apTheme, `${props.colorScheme}.contrast`, baseColor.isDark() ? `white` : 'black'),
              bg: `${baseColor}`,
              border: `2px solid`,
              borderColor: getColor(apTheme, `${props.colorScheme}.focus`, `${props.colorScheme}.600`),
              boxShadow: "unset",
              borderRadius: "lg",
              _active: {
                bg: `${active}`,
              },
              _hover: {
                bg: `${highlight}`,
                borderColor: `${shadowHighlight}`,
              },
              _focus: {
                bg: `${baseColor}`,
              }
            }
            return mergeDeep(theme.components.Input.variants.outline(props), { field });
          }
        }
      },
      Textarea: {
        baseStyle: {
          borderRadius: "lg",
          minHeight: "0",
          lineHeight: "1.6",
          maxWidth: "100%",
          verticalAlign: "middle",
          padding: "0.45rem 1rem !important",
          overflow: "hidden",
          _placeholder: {
            verticalAlign: "bottom",
            fontSize: "1em"
          }
        },
        variants: {
          outline: (props: StyleFunctionProps) => {
            const baseColor = tinycolor(getColor(props.theme, `${props.colorScheme}.fg`, `${props.colorScheme}.500`))

            const shadowHighlight = baseColor.getLuminance() < 0.4 ? baseColor.lighten(10) : baseColor.darken(7);

            const bgColor = tinycolor(colors.white);
            const highlight = bgColor.darken(5);
            const active = bgColor.darken(15);

            const field = {
              color: getColor(apTheme, `${props.colorScheme}.contrast`, baseColor.isDark() ? `white` : 'black'),
              bg: `${baseColor}`,
              border: `2px solid`,
              borderColor: getColor(apTheme, `${props.colorScheme}.focus`, `${props.colorScheme}.600`),
              boxShadow: "unset",
              borderRadius: "lg",
              _active: {
                bg: `${active}`,
              },
              _hover: {
                bg: `${highlight}`,
                borderColor: `${shadowHighlight}`,
              },
              _focus: {
                bg: `${baseColor}`,
              }
            }
            return mergeDeep(props.theme.components.Input.variants.outline(props).field, field);
          },
          flushed: (props: StyleFunctionProps) => {
            return mergeDeep(props.theme.components.Input.variants.flushed(props).field, {});

          }
        }
      },
      Tabs: {
        parts: ["tab", "tablist", "tabpanels", "tabpanel"],
        baseStyle: {
          tablist: {
            maxHeight: "2.5rem",
            marginTop: "auto",
            marginBottom: "0",
            gap: 2,
          },
          tabpanels: {
            height: "100%",
            maxHeight: "calc(100% - 2.5rem)",
          },
        },
        variants: {
          enclosed: (props: StyleFunctionProps) => {
            return {
              tab: {
                // @ts-ignore
                ...theme.components.Tabs.variants.enclosed.tab,
                borderRadius: "1rem 1rem 0 0",
                WebkitTapHighlightColor: "rgba(0,0,0,0)",
                border: "2px solid",
                borderColor: getColor(apTheme, `${props.colorScheme}.solid`, `${props.colorScheme}.400`),
                bg: getColor(apTheme, `${props.colorScheme}.solid`, `${props.colorScheme}.400`),
                borderBottom: "0",
                _selected: {
                  bg: getColor(apTheme, `${props.colorScheme}.subtle`, `${props.colorScheme}.100`),
                  color: "",
                },
                _focus: {
                  bg: getColor(apTheme, `${props.colorScheme}.muted`, `${props.colorScheme}.200`),
                  boxShadow: "outline",
                },
                _notLast: {
                  marginEnd: "-1px",
                },
              },
              tablist: {
                padding: props.isFitted ? "0 1rem" : "0",
                height: "min-content",
                maxHeight: "4rem",
                width: "95%",
                marginTop: "auto",
                gap: 2,
                borderBottom: "0",
                borderColor: getColor(apTheme, `${props.colorScheme}.solid`, `${props.colorScheme}.400`)
              },
              tabpanels: {
                height: "100%",
                maxHeight: "calc(100% - 4rem)",
                border: "2px solid",
                borderColor: getColor(apTheme, `${props.colorScheme}.solid`, `${props.colorScheme}.400`),
                borderRadius: props.isFitted ? "1rem" : "0 1rem 1rem 1rem",
                bg: getColor(apTheme, `${props.colorScheme}.subtle`, `${props.colorScheme}.100`),
              },
            }
          },
          solidEnclosed: (props: StyleFunctionProps) => {
            return {
              tab: {
                borderRadius: "1rem 1rem 0 0",
                border: "none",
                bg: getColor(apTheme, `${props.colorScheme}.solid`, `${props.colorScheme}.400`),
                borderBottom: "0",
                _selected: {
                  bg: getColor(apTheme, `${props.colorScheme}.fg`, `${props.colorScheme}.500`),
                  color: "",
                },
                _focus: {
                  bg: getColor(apTheme, `${props.colorScheme}.solid`, `${props.colorScheme}.400`),
                },
                _notLast: {
                  marginEnd: "-1px",
                },
              },
              tablist: {
                padding: "0 1rem",
                gap: 2,
                border: "none",
              },
              tabpanels: {
                flexGrow: 1,
                border: "none",
                borderRadius: "1rem",
                bg: getColor(apTheme, `${props.colorScheme}.fg`, `${props.colorScheme}.500`)
              }
            }
          }
        }
      },
      Flex: {
        variants: {
          footer: (props: StyleFunctionProps) => ({
            width: "100%",
            display: "flex",
            flexDirection: "row",
            gap: "1rem",
            marginBottom: "1rem",
            padding: "0 1rem",
          }),
        }
      }
    },
    styles,
  })
;

export default apTheme;
